/**
 * SQ Before/After — Desktop conveyor-belt carousel
 *
 * Desktop (≥768px): All cards in a single horizontal track, translated
 * via translateX to center the current card. Current card scales up,
 * adjacent cards are smaller + faded. Arrows and swipe/drag to advance.
 * Stops at edges (first/last card). Viewport clips to 3 cards.
 *
 * Mobile (<768px): no-op — native horizontal scroll handles it.
 * Pointer listeners are only attached on desktop to avoid blocking
 * native touch scroll.
 */
(function () {
  function init() {
    var section = document.querySelector('.sq-before-after');
    if (!section) return;

    var scroll = section.querySelector('.baa-scroll');
    if (!scroll) return;
    var cards = Array.from(scroll.querySelectorAll('.baa-card'));
    var n = cards.length;
    if (n < 2) return;

    var wrap = section.querySelector('.baa-carousel-wrap');
    if (!wrap) return;

    var mql = window.matchMedia('(min-width: 768px)');
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Start on second card so flanking cards are visible
    var current = n >= 3 ? 1 : 0;

    // Create accessible arrow buttons
    var prevBtn = createArrow('prev', 'B&A anterior');
    var nextBtn = createArrow('next', 'Próximo B&A');
    var sectionEl = wrap.parentElement;
    sectionEl.insertBefore(prevBtn, wrap);
    sectionEl.insertBefore(nextBtn, wrap.nextSibling);

    function createArrow(dir, label) {
      var btn = document.createElement('button');
      btn.className = 'baa-arrow baa-arrow--' + dir;
      btn.setAttribute('aria-label', label);
      btn.setAttribute('type', 'button');
      btn.innerHTML = dir === 'prev'
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>'
        : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>';
      return btn;
    }

    // Dot position indicator
    var dotsWrap = document.createElement('div');
    dotsWrap.className = 'baa-dots';
    dotsWrap.setAttribute('role', 'tablist');
    dotsWrap.setAttribute('aria-label', 'B&A cards');
    var dots = [];
    for (var i = 0; i < n; i++) {
      var dot = document.createElement('button');
      dot.className = 'baa-dot';
      dot.setAttribute('type', 'button');
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', 'Card ' + (i + 1) + ' de ' + n);
      dot.addEventListener('click', (function (idx) {
        return function () {
          current = idx;
          update(true);
        };
      })(i));
      dots.push(dot);
      dotsWrap.appendChild(dot);
    }
    wrap.appendChild(dotsWrap);

    function updateDots() {
      dots.forEach(function (d, idx) {
        var active = idx === current;
        d.classList.toggle('is-active', active);
        d.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    function getStep() {
      var cardW = cards[0].offsetWidth;
      var gap = parseFloat(getComputedStyle(scroll).gap) || 0;
      return { cardW: cardW, gap: gap, step: cardW + gap };
    }

    function clamp(val) {
      return Math.max(0, Math.min(val, n - 1));
    }

    function collapseExpanded() {
      scroll.querySelectorAll('.baa-quote[data-expanded]').forEach(function (q) {
        var truncated = q.getAttribute('data-truncated');
        if (!truncated) return;
        var closeQuote = '<span class="baa-quote-close">”</span>';
        q.innerHTML = truncated + closeQuote + ' <a href="javascript:void(0)" class="baa-read-more">ler mais</a>';
        q.removeAttribute('data-expanded');
      });
    }

    function update(animate) {
      if (!mql.matches) {
        // Mobile: clear all desktop state, let native scroll work
        scroll.style.transform = '';
        scroll.style.transition = '';
        cards.forEach(function (c) { c.classList.remove('is-current'); });
        prevBtn.disabled = false;
        nextBtn.disabled = false;
        prevBtn.style.top = '';
        nextBtn.style.top = '';
        return;
      }

      current = clamp(current);
      collapseExpanded();

      var m = getStep();
      var viewW = wrap.offsetWidth;
      var padL = parseFloat(getComputedStyle(wrap).paddingLeft) || 0;
      var offset = (viewW - m.cardW) / 2 - padL - current * m.step;

      if (animate && !reducedMotion.matches) {
        scroll.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
      } else {
        scroll.style.transition = 'none';
      }
      scroll.style.transform = 'translateX(' + offset + 'px)';

      cards.forEach(function (c, idx) {
        c.classList.toggle('is-current', idx === current);
      });

      prevBtn.disabled = current === 0;
      nextBtn.disabled = current === n - 1;

      updateDots();

      var arrowTop = wrap.offsetTop + wrap.offsetHeight / 2;
      prevBtn.style.top = arrowTop + 'px';
      nextBtn.style.top = arrowTop + 'px';
    }

    prevBtn.addEventListener('click', function () {
      if (current > 0) { current--; update(true); }
    });
    nextBtn.addEventListener('click', function () {
      if (current < n - 1) { current++; update(true); }
    });

    // Keyboard arrow navigation on focused arrow buttons
    function onArrowKeydown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (current > 0) { current--; update(true); }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (current < n - 1) { current++; update(true); }
      } else if (e.key === 'Home') {
        e.preventDefault();
        current = 0;
        update(true);
      } else if (e.key === 'End') {
        e.preventDefault();
        current = n - 1;
        update(true);
      }
    }
    prevBtn.addEventListener('keydown', onArrowKeydown);
    nextBtn.addEventListener('keydown', onArrowKeydown);

    // Swipe/drag — desktop only. Listeners are attached/detached on
    // breakpoint change to avoid blocking native touch scroll on mobile.
    var dragStartX = 0;
    var trackStartX = 0;
    var dragging = false;
    var SWIPE_THRESHOLD = 50;

    function onPointerDown(e) {
      if (e.target.closest('a, button')) return;
      dragging = true;
      dragStartX = e.clientX;
      var matrix = getComputedStyle(scroll).transform;
      if (matrix && matrix !== 'none') {
        trackStartX = parseFloat(matrix.split(',')[4]) || 0;
      } else {
        trackStartX = 0;
      }
      scroll.setPointerCapture(e.pointerId);
      scroll.style.transition = 'none';
      scroll.style.cursor = 'grabbing';
    }

    function onPointerMove(e) {
      if (!dragging) return;
      e.preventDefault();
      var delta = e.clientX - dragStartX;
      scroll.style.transform = 'translateX(' + (trackStartX + delta) + 'px)';
    }

    function onPointerUp(e) {
      if (!dragging) return;
      dragging = false;
      scroll.style.cursor = '';
      var delta = e.clientX - dragStartX;
      if (Math.abs(delta) >= SWIPE_THRESHOLD) {
        if (delta < 0 && current < n - 1) current++;
        else if (delta > 0 && current > 0) current--;
      }
      update(true);
    }

    function onPointerCancel() {
      if (!dragging) return;
      dragging = false;
      scroll.style.cursor = '';
      update(true);
    }

    function attachDrag() {
      scroll.addEventListener('pointerdown', onPointerDown);
      scroll.addEventListener('pointermove', onPointerMove);
      scroll.addEventListener('pointerup', onPointerUp);
      scroll.addEventListener('pointercancel', onPointerCancel);
    }

    function detachDrag() {
      scroll.removeEventListener('pointerdown', onPointerDown);
      scroll.removeEventListener('pointermove', onPointerMove);
      scroll.removeEventListener('pointerup', onPointerUp);
      scroll.removeEventListener('pointercancel', onPointerCancel);
      dragging = false;
      scroll.style.cursor = '';
    }

    if (mql.matches) attachDrag();

    mql.addEventListener('change', function () {
      if (mql.matches) attachDrag();
      else detachDrag();
      update(false);
    });

    window.addEventListener('resize', function () {
      update(false);
    });

    update(false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
